// Thin fetch wrapper. The Console talks to the same dashboard API at /api/console/*.
// Session token (magic-link issued JWT) lives in localStorage under `titan.console.token`.

const TOKEN_KEY = 'titan.console.token';

export const session = {
  get token() { return localStorage.getItem(TOKEN_KEY); },
  set token(v) { v ? localStorage.setItem(TOKEN_KEY, v) : localStorage.removeItem(TOKEN_KEY); },
  clear() { localStorage.removeItem(TOKEN_KEY); },
};

export async function apiFetch(path, { method = 'GET', body, headers = {} } = {}) {
  const res = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(session.token ? { Authorization: `Bearer ${session.token}` } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    session.clear();
    // Send to login; React Router rerender will pick it up on next nav.
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Binary file download with Authorization header.
 * Native <a href> cannot send auth headers, so PDF endpoints return 401.
 * This helper fetches the file authenticated, creates a temporary object URL,
 * and triggers a browser download — then immediately revokes the URL.
 *
 * @param {string} path - API path (e.g. /api/console/reports/123/pdf)
 * @param {string} filename - suggested filename for the download dialog
 */
export async function apiDownload(path, filename) {
  const res = await fetch(path, {
    headers: {
      ...(session.token ? { Authorization: `Bearer ${session.token}` } : {}),
    },
  });

  if (res.status === 401) {
    session.clear();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const text = await res.text();
    let data = null;
    try { data = JSON.parse(text); } catch { /* non-JSON body */ }
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
